import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Save } from "lucide-react";
import { api, ApiError } from "../../api/client";
import type { MyProfile } from "../../api/types";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import { ChangePasswordDialog } from "../auth/ChangePasswordDialog";
import { useAuth } from "../auth/useAuth";
import { ROLE_OPTIONS } from "../users/UserList";
import { MailboxSettings } from "../mailbox/MailboxSettings";

// 設定 → 個人資料。每個登入者都有這個分頁(業主 2026-09-25)。
//
// - 姓名、電話、Email、現居地址自己改得動;職稱、年度特休與入職日只能看 ——
//  「年度特休/角色 不能修改 其他都可以」,入職日由老闆設(業主 2026-09-25)。
//   那幾項要改找老闆,從使用者分頁改。「角色」在 2026-09-25 統一改稱「職稱」。
// - **改 Email 要再打一次目前密碼**,而且那一欄只在 Email 真的被改了才出現。
//   Email 就是登入帳號,理由見後端 users.update_my_profile。
// - 失敗絕不清掉表單;錯誤訊息照抄後端,不回吐使用者打的密碼。

const PROFILE_KEY = ["my-profile"];

interface Form {
  name: string;
  phone: string;
  email: string;
  home_address: string;
}

function roleLabel(role: MyProfile["role"]): string {
  return ROLE_OPTIONS.find((option) => option.value === role)?.label ?? role;
}

export function ProfileSettings() {
  const { push } = useToast();
  const { refresh } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Form>({ name: "", phone: "", email: "", home_address: "" });
  const [currentPassword, setCurrentPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: PROFILE_KEY,
    queryFn: () => api.get<MyProfile>("/users/me/profile"),
  });

  useEffect(() => {
    if (!data) return;
    setForm({ name: data.name, phone: data.phone, email: data.email, home_address: data.home_address ?? "" });
    setCurrentPassword("");
  }, [data]);

  const emailChanged =
    data !== undefined && form.email.trim().toLowerCase() !== data.email.toLowerCase();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    if (emailChanged && currentPassword === "") {
      setError("改 Email 要先輸入目前密碼");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const body = emailChanged ? { ...form, current_password: currentPassword } : form;
      const saved = await api.patch<MyProfile>("/users/me/profile", body);
      queryClient.setQueryData(PROFILE_KEY, saved);
      push(emailChanged ? "已儲存。下次登入請用新的 Email。" : "已儲存", "success");
      // 側邊欄的名字與 Email 來自 /auth/me,改完要重新問一次
      await refresh?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "無法連線到伺服器,請稍後再試。");
    } finally {
      setSaving(false);
    }
  };

  const set = (key: keyof Form) => (event: { target: { value: string } }) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  return (
    <>
      <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4">
        {isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
        {loadError ? (
          <p role="alert" className="m-0 text-sm text-[var(--danger)]">
            個人資料載入失敗,請稍後再試。
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="姓名" name="name" required value={form.name} onChange={set("name")} />
          <Input label="電話" name="phone" value={form.phone} onChange={set("phone")} />
          <Input
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            required
            hint="也是你的登入帳號"
            value={form.email}
            onChange={set("email")}
          />
          {emailChanged ? (
            <Input
              label="目前密碼"
              name="current_password"
              type="password"
              autoComplete="current-password"
              hint="改 Email 要再確認一次是你本人"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          ) : null}
          <div className="sm:col-span-2">
            <Input
              label="現居地址"
              name="home_address"
              autoComplete="street-address"
              value={form.home_address}
              onChange={set("home_address")}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input label="職稱" name="role" readOnly value={data ? roleLabel(data.role) : ""} />
          <Input
            label="年度特休"
            name="annual_leave_days"
            readOnly
            value={data ? `${Number(data.annual_leave_days)} 天` : ""}
          />
          <Input label="入職日" name="hire_date" readOnly value={data?.hire_date ?? ""} />
        </div>
        <p className="m-0 text-xs text-[var(--text-3)]">職稱、年度特休與入職日要改請找老闆。</p>

        {error ? (
          <p role="alert" className="m-0 text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" loading={saving} icon={<Save aria-hidden="true" size={16} />}>
            儲存個人資料
          </Button>
          <Button type="button" icon={<KeyRound aria-hidden="true" size={16} />} onClick={() => setPasswordOpen(true)}>
            修改密碼
          </Button>
        </div>
      </form>
      {/* 我的信箱(個人信箱第二階段):啟用 / 換密碼 / 停用 / 簽名檔。自己有 <form>,放在外面 */}
      <MailboxSettings />
      {/* 對話框有自己的 <form>,放在外面才不會變成巢狀表單 */}
      <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </>
  );
}
